import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  ConfirmDialogConfig,
  AlertDialogConfig,
  InputDialogConfig,
  DialogResult
} from '../../interfaces/dialog.interface';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from './alert-dialog/alert-dialog.component';
import { InputDialogComponent } from './input-dialog/input-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private dialog = inject(MatDialog);


  /**
   * Muestra un diálogo de confirmación
   * @param config Configuración del diálogo
   * @returns Observable con el resultado del diálogo
   */
  confirm(config: ConfirmDialogConfig): Observable<DialogResult> {
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      width: config.width || '400px',
      disableClose: config.disableClose || false,
      panelClass: 'gradient-header-dialog',
      data: config
    });

    return dialogRef.afterClosed();
  }

  /**
   * Muestra un diálogo de alerta/información
   * @param config Configuración del diálogo
   * @returns Observable con el resultado del diálogo
   */
  alert(config: AlertDialogConfig): Observable<DialogResult> {
    const dialogRef: MatDialogRef<AlertDialogComponent> = this.dialog.open(AlertDialogComponent, {
      width: config.width || '500px',
      disableClose: config.disableClose || false,
      panelClass: 'gradient-header-dialog',
      data: config
    });

    return dialogRef.afterClosed();
  }

  /**
   * Muestra un diálogo con input para capturar datos
   * @param config Configuración del diálogo
   * @returns Observable con el resultado del diálogo
   */
  input(config: InputDialogConfig): Observable<DialogResult> {
    const dialogRef: MatDialogRef<InputDialogComponent> = this.dialog.open(InputDialogComponent, {
      width: config.width || '500px',
      disableClose: config.disableClose || false,
      autoFocus: 'first-tabbable',
      panelClass: 'gradient-header-dialog',
      data: config
    });

    return dialogRef.afterClosed();
  }

  /**
   * Método de conveniencia para confirmación de eliminación
   * @param itemName Nombre del elemento a eliminar
   * @param additionalMessage Mensaje adicional opcional
   * @returns Observable con el resultado del diálogo
   */
  confirmDelete(itemName: string, additionalMessage?: string): Observable<DialogResult> {
    const message = additionalMessage 
      ? `¿Deseas eliminar ${itemName}?\n\n${additionalMessage}\n\nEsta acción no se puede deshacer.`
      : `¿Deseas eliminar ${itemName}?\n\nEsta acción no se puede deshacer.`;

    return this.confirm({
      title: 'Confirmar Eliminación',
      message: message,
      icon: 'delete',
      type: 'confirm',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      destructive: true
    });
  }

  /**
   * Método de conveniencia para mostrar éxito
   * @param message Mensaje de éxito
   * @returns Observable con el resultado del diálogo
   */
  success(message: string): Observable<DialogResult> {
    return this.alert({
      title: 'Éxito',
      message: message,
      icon: 'check_circle',
      type: 'success',
      confirmText: 'Aceptar'
    });
  }

  /**
   * Método de conveniencia para mostrar error
   * @param message Mensaje de error
   * @returns Observable con el resultado del diálogo
   */
  error(message: string): Observable<DialogResult> {
    return this.alert({
      title: 'Error',
      message: message,
      icon: 'error',
      type: 'error',
      confirmText: 'Aceptar'
    });
  }

  /**
   * Método de conveniencia para mostrar advertencia
   * @param message Mensaje de advertencia
   * @returns Observable con el resultado del diálogo
   */
  warning(message: string): Observable<DialogResult> {
    return this.alert({
      title: 'Advertencia',
      message: message,
      icon: 'warning',
      type: 'warning',
      confirmText: 'Aceptar'
    });
  }

  /**
   * Método de conveniencia para mostrar información
   * @param message Mensaje informativo
   * @returns Observable con el resultado del diálogo
   */
  info(message: string): Observable<DialogResult> {
    return this.alert({
      title: 'Información',
      message: message,
      icon: 'info',
      type: 'info',
      confirmText: 'Aceptar'
    });
  }

  /**
   * Método de conveniencia para capturar texto
   * @param title Título del diálogo
   * @param label Etiqueta del input
   * @param placeholder Placeholder del input
   * @param currentValue Valor actual (para edición)
   * @returns Observable con el resultado del diálogo
   */
  promptText(title: string, label: string, placeholder?: string, currentValue?: string): Observable<DialogResult> {
    return this.input({
      title: title,
      message: '',
      inputLabel: label,
      inputType: 'text',
      inputPlaceholder: placeholder,
      inputValue: currentValue,
      required: true,
      type: 'input',
      confirmText: 'Aceptar',
      cancelText: 'Cancelar'
    });
  }
}